import { ArrowDownToLine, ArrowUpToLine, Plus, UnfoldVertical } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { quoteAppearsIn } from '@repo/shared-types';
import { cn } from '../../lib/utils';

export type DiffToken = { type: 'same' | 'add' | 'remove'; value: string };

function lcsDiff<T>(a: T[], b: T[]): DiffToken[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const tokens: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      tokens.push({ type: 'same', value: String(a[i]) });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      tokens.push({ type: 'remove', value: String(a[i]) });
      i++;
    } else {
      tokens.push({ type: 'add', value: String(b[j]) });
      j++;
    }
  }
  while (i < n) { tokens.push({ type: 'remove', value: String(a[i]) }); i++; }
  while (j < m) { tokens.push({ type: 'add', value: String(b[j]) }); j++; }
  return tokens;
}

function tokenizeWords(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t.length > 0);
}

/**
 * Word-level difference between two short passages, as runs of same / removed / added.
 *
 * Exported for the quality view, which uses it on a pair that is not a before and after: two pages
 * rendering one source sentence, where `remove` is what only the first page says and `add` what
 * only the second does. That is the whole question a duplicate review asks, so the tokens are read
 * as "unique to this side" rather than as an edit.
 */
export function diffWords(oldText: string, newText: string): DiffToken[] {
  const tokens = lcsDiff(tokenizeWords(oldText), tokenizeWords(newText));
  return tokens.reduce<DiffToken[]>((merged, tok) => {
    const last = merged[merged.length - 1];
    if (last && last.type === tok.type) last.value += tok.value;
    else merged.push({ ...tok });
    return merged;
  }, []);
}

export interface DiffLine {
  type: 'same' | 'add' | 'remove';
  oldLineNo: number | null;
  newLineNo: number | null;
  text: string;
  wordTokens?: DiffToken[];
}

export function buildLineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const tokens = lcsDiff(oldLines, newLines);

  const result: DiffLine[] = [];
  let oldNo = 1;
  let newNo = 1;
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t.type === 'same') {
      result.push({ type: 'same', oldLineNo: oldNo++, newLineNo: newNo++, text: t.value });
      i++;
      continue;
    }

    // Collect one contiguous run of removes/adds so 1:1 replacements can be paired for word-level highlighting.
    const removes: string[] = [];
    const adds: string[] = [];
    while (i < tokens.length && tokens[i].type !== 'same') {
      if (tokens[i].type === 'remove') removes.push(tokens[i].value);
      else adds.push(tokens[i].value);
      i++;
    }

    const pairCount = Math.min(removes.length, adds.length);
    for (let k = 0; k < pairCount; k++) {
      const wordTokens = diffWords(removes[k], adds[k]);
      result.push({ type: 'remove', oldLineNo: oldNo++, newLineNo: null, text: removes[k], wordTokens });
      result.push({ type: 'add', oldLineNo: null, newLineNo: newNo++, text: adds[k], wordTokens });
    }
    for (let k = pairCount; k < removes.length; k++) {
      result.push({ type: 'remove', oldLineNo: oldNo++, newLineNo: null, text: removes[k] });
    }
    for (let k = pairCount; k < adds.length; k++) {
      result.push({ type: 'add', oldLineNo: null, newLineNo: newNo++, text: adds[k] });
    }
  }
  return result;
}

export function OldLineWords({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.filter((t) => t.type !== 'add').map((t, i) =>
        t.type === 'remove'
          ? <span key={i} className="rounded-[3px] bg-red-200 dark:bg-red-800/70 text-red-950 dark:text-white">{t.value}</span>
          : <span key={i}>{t.value}</span>,
      )}
    </>
  );
}

export function NewLineWords({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.filter((t) => t.type !== 'remove').map((t, i) =>
        t.type === 'add'
          ? <span key={i} className="bg-green-200/70 dark:bg-green-900/50 text-green-900 dark:text-green-300">{t.value}</span>
          : <span key={i}>{t.value}</span>,
      )}
    </>
  );
}

/**
 * Added/removed line counts for a change, for showing `+12 −3` next to a collapsed entry.
 *
 * Shares `buildLineDiff` with the renderer so a summary can never disagree with the diff it
 * summarises. Very large inputs are reported as a wholesale replacement rather than run through
 * the O(n·m) table — a history view computes this for every transition at once, and a pasted
 * dump shouldn't stall the list.
 */
export function lineDiffStats(oldText: string, newText: string): { added: number; removed: number } {
  const MAX_LINES = 1500;
  const oldLines = oldText === '' ? 0 : oldText.split('\n').length;
  const newLines = newText === '' ? 0 : newText.split('\n').length;
  if (oldLines > MAX_LINES || newLines > MAX_LINES) return { added: newLines, removed: oldLines };

  const lines = buildLineDiff(oldText, newText);
  return {
    added: lines.filter((l) => l.type === 'add').length,
    removed: lines.filter((l) => l.type === 'remove').length,
  };
}

/** Unchanged lines kept visible on each side of a change, so every hunk keeps some context. */
const CONTEXT_LINES = 3;
/** Lines revealed per click of a directional chevron. */
const EXPAND_STEP = 10;
/** Below this, a run of unchanged lines is cheaper to show than to replace with an expander bar. */
const MIN_COLLAPSED = 4;

/** Inclusive [from, to] index ranges into the diff-line array that start out hidden. */
function collapsibleRanges(lines: DiffLine[], context: number): Array<[number, number]> {
  // With nothing changed there is no hunk to anchor context to, so collapsing would hide the whole
  // body behind a single bar. Show it as-is instead.
  if (!lines.some((l) => l.type !== 'same')) return [];

  const ranges: Array<[number, number]> = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].type !== 'same') { i++; continue; }
    let j = i;
    while (j < lines.length && lines[j].type === 'same') j++;
    // A leading/trailing run only needs context on the side that faces a change.
    const from = i === 0 ? i : i + context;
    const to = j === lines.length ? j - 1 : j - 1 - context;
    if (to - from + 1 >= MIN_COLLAPSED) ranges.push([from, to]);
    i = j;
  }
  return ranges;
}

/** Lines already revealed off each end of a collapsed range: `down` from its top, `up` from its bottom. */
type Expansion = { up: number; down: number };

/** Shared identity so "nothing expanded yet" doesn't invalidate the row memo on every render. */
const NO_EXPANSION: Record<string, Expansion> = {};

function ExpanderRow({
  rowKey,
  count,
  oldFirst,
  newFirst,
  onExpandAll,
  onExpandDown,
  onExpandUp,
}: {
  /** Published as `data-diff-row`: a margin note whose line is folded away aligns with this. */
  rowKey: string;
  count: number;
  /** Old/new line numbers of the first hidden line — what the @@ header is derived from. */
  oldFirst: number | null;
  newFirst: number | null;
  onExpandAll: () => void;
  onExpandDown: () => void;
  onExpandUp: () => void;
}) {
  const step = Math.min(EXPAND_STEP, count);

  // GitHub's expander, both halves: a solid blue gutter block holding the fold icons — stacked
  // when the gap is big enough to expand from either end — and beside it a quieter blue band
  // carrying the classic @@ hunk header, which is derivable exactly because every hidden line is
  // an unchanged line and so carries both of its numbers.
  // Each button is at least one diff line tall (12.5px × 1.65 ≈ 21px) — a split expander is two
  // stacked targets, and half a line each was too thin to hit.
  const iconButton =
    'flex flex-1 min-h-[21px] items-center justify-center text-blue-700 dark:text-blue-200 hover:bg-blue-500 hover:text-white transition-colors';
  const hunkHeader =
    oldFirst !== null && newFirst !== null ? `@@ -${oldFirst},${count} +${newFirst},${count} @@` : null;

  return (
    <div data-diff-row={rowKey} className="flex items-stretch">
      {/* Exactly the two w-9 number columns (4.5rem), ending on the same divider they do — any
          wider and the block bleeds under the +/- marker column and reads as misaligned. */}
      <div className="w-[4.5rem] shrink-0 flex flex-col items-stretch bg-blue-200/80 dark:bg-blue-800/60 border-r border-border-subtle">
        {count > EXPAND_STEP ? (
          <>
            {/* Top expands downward from the block above; bottom expands upward from the block
                below — the same pairing GitHub uses, icon pointing at the fold it eats into. */}
            <button
              type="button"
              onClick={onExpandDown}
              title={`Show ${step} lines from the top of this gap`}
              aria-label={`Show ${step} lines from the top of this gap`}
              className={iconButton}
            >
              <ArrowDownToLine size={12} />
            </button>
            <button
              type="button"
              onClick={onExpandUp}
              title={`Show ${step} lines from the bottom of this gap`}
              aria-label={`Show ${step} lines from the bottom of this gap`}
              className={iconButton}
            >
              <ArrowUpToLine size={12} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onExpandAll}
            title="Show unchanged lines"
            aria-label="Show unchanged lines"
            className={iconButton}
          >
            <UnfoldVertical size={13} />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onExpandAll}
        title={`Show ${count} unchanged ${count === 1 ? 'line' : 'lines'}`}
        className="flex-1 flex items-center text-left px-3 bg-blue-50 dark:bg-blue-950/40 text-ink-faint dark:text-blue-300/60 hover:text-ink dark:hover:text-blue-200 transition-colors"
      >
        <span className="truncate">
          {hunkHeader ?? `${count} unchanged ${count === 1 ? 'line' : 'lines'}`}
        </span>
      </button>
    </div>
  );
}

type DiffRow =
  | { kind: 'line'; key: string; index: number; line: DiffLine; after?: ReactNode[] }
  | { kind: 'gap'; key: string; rangeKey: string; count: number; oldFirst: number | null; newFirst: number | null };

/** A margin note, resolved to the row it sits beside and the line that put it there. */
interface MarginPlacement {
  key: string;
  /** The `data-diff-row` of the visible row to align with — a line, or the expander hiding it. */
  rowKey: string;
  /** The matched line's index, which is document order and so the order notes must stack in. */
  lineIndex: number;
  node: ReactNode;
}

/** A line of the diff a caller wants to attach something to, addressed by its verbatim text. */
export interface DiffLineTarget {
  /** The line's text, exactly as the diff shows it. */
  quote: string;
  /** True when it is a removed line — the same text can exist on both sides of a replacement. */
  removed: boolean;
}

/** Something rendered inline beneath its matched line — a review comment, a composer. */
export interface DiffAnnotation extends DiffLineTarget {
  key: string;
  node: ReactNode;
}

/** First diff line matching a target. Removed lines only match removed targets, and vice versa. */
function matchLine(lines: DiffLine[], target: DiffLineTarget): number {
  return lines.findIndex(
    (line) => line.text === target.quote && (target.removed ? line.type === 'remove' : line.type !== 'remove'),
  );
}

/**
 * Something rendered in the right margin, beside the line it refers to — a citation, in practice.
 *
 * Anchored on quoted text like `DiffAnnotation`, and for the same reason, but matched as a
 * *substring* of a line rather than as a whole one: a comment is pinned to a line the reviewer
 * clicked, while a citation's `nodeQuote` is a sentence inside a paragraph and almost never equal
 * to the line holding it.
 */
export interface DiffMarginNote {
  key: string;
  /** Verbatim body text this note is evidence for. */
  quote: string;
  node: ReactNode;
}

/** Vertical breathing room between two stacked margin notes, in px. */
const MARGIN_GAP = 8;

/**
 * The line a margin note belongs beside, or -1.
 *
 * Matched with the shared `quoteAppearsIn` rather than a comparison of its own, because this is the
 * same question the commit gate and the currency check ask and there is deliberately one
 * implementation of it (see @repo/shared-types/citations). A whitespace-only compare would have
 * been the third copy and the weakest: a `nodeQuote` is copied from the markdown *source*, so it
 * arrives carrying `**bold**` and backticks, and stripping that syntax is worth a third of the
 * citations on real agent output — the failure that cost run `cmtbt9jfq` 25 of its 26.
 *
 * Two passes, because a quote and a line can contain each other either way round. Usually the line
 * is the longer — a paragraph holding the cited sentence — but a quote spanning several lines is
 * contained by none of them, and then the honest anchor is the first line the quote covers, which
 * is where the cited passage begins. Removed lines never match: evidence backs the text as it now
 * stands, and a quote found only in deleted text backs a claim the page has stopped making.
 */
export function matchQuoteLine(lines: DiffLine[], quote: string): number {
  if (quote.trim().length === 0) return -1;

  const containing = lines.findIndex((line) => line.type !== 'remove' && quoteAppearsIn(quote, line.text));
  if (containing !== -1) return containing;

  return lines.findIndex(
    (line) => line.type !== 'remove' && line.text.trim().length > 0 && quoteAppearsIn(line.text, quote),
  );
}

/**
 * Git-style unified diff: dual line-number gutter, +/- markers, word-level highlight within replaced lines.
 *
 * Long runs of unchanged text collapse behind an expander bar so a reviewer reads only what moved —
 * a one-line amendment to a long KB body otherwise arrives buried in identical prose. Each bar
 * expands in either direction a step at a time, or fully from its label.
 */
export function LineDiffView({
  oldText,
  newText,
  collapseUnchanged = true,
  contextLines = CONTEXT_LINES,
  onCommentLine,
  annotations,
  margin,
  marginHost,
}: {
  oldText: string;
  newText: string;
  collapseUnchanged?: boolean;
  contextLines?: number;
  /**
   * When given, every line grows a hover affordance to comment on it — GitHub-review-shaped. The
   * target is the line's verbatim text, not its number, so what the caller stores survives the
   * page moving on (the same reason citations anchor on quotes).
   */
  onCommentLine?: (target: DiffLineTarget) => void;
  /**
   * Rendered inline beneath their matched lines — review comments, an open composer. An annotation
   * whose line is collapsed or gone renders in a footer instead of vanishing: a comment the
   * reviewer wrote must never be invisible.
   */
  annotations?: DiffAnnotation[];
  /**
   * Rendered in a right-hand margin beside the line each one quotes, Google-Docs-shaped: a note
   * floats level with its anchor, pushed down only far enough to clear the note above it.
   *
   * Unlike an annotation, a note whose anchor is inside a collapsed gap aligns with the *expander*
   * rather than falling through to the footer — evidence that jumped between two places as folds
   * were opened would read as evidence appearing and disappearing. Only a quote matching no line
   * at all goes to the footer, and that depends on the text alone, so it does not move.
   */
  margin?: DiffMarginNote[];
  /**
   * Where to render that margin — a host element the *caller* positions, which the notes are
   * portalled into.
   *
   * A portal rather than a column of this component's own, because the margin belongs outside the
   * container the diff sits in: in KB Review the diff is inside a bordered card with its own
   * padding, and evidence has to sit beside that card rather than inside it. Nothing in this
   * subtree can render there. Keeping the matching and the geometry here anyway is what avoids the
   * alternative — the caller resolving quotes to lines itself, which would mean diffing the text a
   * second time just to learn where the lines are.
   *
   * Positions are measured relative to the host, so the caller owes it `position: relative` and a
   * top edge level with this diff. No host means no margin: every note goes to the footer.
   */
  marginHost?: HTMLElement | null;
}) {
  const lines = useMemo(() => buildLineDiff(oldText, newText), [oldText, newText]);

  /**
   * Each margin note resolved to the line it quotes — the expensive half, kept in its own memo.
   *
   * Deliberately *not* folded into the row builder below, which also depends on `annotations` and
   * on which folds are open: a caller rebuilds `annotations` inline, so that memo recomputes on
   * every render, and `quoteAppearsIn`'s markdown fallback over every line of a long page is far
   * too much work to repeat on each keystroke in a comment composer. Which line a quote matches
   * cannot change without the text or the notes changing, so these are the only two dependencies.
   */
  const anchors = useMemo(
    () =>
      // With nowhere to render a margin there is nothing to anchor: every note goes to the footer,
      // which is why the matching is skipped entirely rather than computed and discarded.
      (marginHost ? margin ?? [] : []).map((note) => ({ note, lineIndex: matchQuoteLine(lines, note.quote) })),
    [lines, margin, marginHost],
  );
  const ranges = useMemo(
    () => (collapseUnchanged ? collapsibleRanges(lines, contextLines) : []),
    [lines, collapseUnchanged, contextLines],
  );

  // Range keys are diff-line indices, so they only mean anything for the diff that produced them.
  // Tying the map to its `ranges` identity discards it when the texts change, rather than reopening
  // whichever new ranges happen to collide with an old key.
  const [expansion, setExpansion] = useState<{ ranges: typeof ranges; map: Record<string, Expansion> }>(
    { ranges, map: NO_EXPANSION },
  );
  const expanded = expansion.ranges === ranges ? expansion.map : NO_EXPANSION;

  const reveal = (key: string, delta: Partial<Expansion>) =>
    setExpansion((prev) => {
      const map = prev.ranges === ranges ? prev.map : NO_EXPANSION;
      const cur = map[key] ?? { up: 0, down: 0 };
      return { ranges, map: { ...map, [key]: { up: cur.up + (delta.up ?? 0), down: cur.down + (delta.down ?? 0) } } };
    });

  const rows = useMemo(() => {
    // Still-hidden ranges after expansion, keyed by the line index they now begin at.
    const gapAt = new Map<number, { rangeKey: string; from: number; to: number }>();
    for (const [from, to] of ranges) {
      const rangeKey = `${from}:${to}`;
      const { up, down } = expanded[rangeKey] ?? { up: 0, down: 0 };
      if (from + down <= to - up) gapAt.set(from + down, { rangeKey, from: from + down, to: to - up });
    }

    // Attach each annotation to its line by verbatim match; whatever cannot be placed — the quote
    // is gone, or its line sits inside a still-collapsed gap — falls through to the footer.
    const attachAt = new Map<number, ReactNode[]>();
    const orphaned: ReactNode[] = [];
    for (const annotation of annotations ?? []) {
      const index = matchLine(lines, annotation);
      if (index === -1) {
        orphaned.push(annotation.node);
        continue;
      }
      attachAt.set(index, [...(attachAt.get(index) ?? []), annotation.node]);
    }

    // Which visible row each line ended up in, so a margin note can align with the expander
    // standing in for a folded line rather than with nothing at all.
    const rowKeyOfLine = new Map<number, string>();

    const out: DiffRow[] = [];
    for (let i = 0; i < lines.length; i++) {
      const gap = gapAt.get(i);
      if (gap) {
        const key = `gap-${gap.rangeKey}`;
        out.push({
          kind: 'gap',
          key,
          rangeKey: gap.rangeKey,
          count: gap.to - gap.from + 1,
          oldFirst: lines[gap.from].oldLineNo,
          newFirst: lines[gap.from].newLineNo,
        });
        // Annotations whose lines are inside the hidden range surface in the footer rather than
        // disappearing with them.
        for (let j = i; j <= gap.to; j++) {
          const hidden = attachAt.get(j);
          if (hidden) orphaned.push(...hidden);
          rowKeyOfLine.set(j, key);
        }
        i = gap.to;
        continue;
      }
      const key = `l-${i}`;
      rowKeyOfLine.set(i, key);
      out.push({ kind: 'line', key, index: i, line: lines[i], after: attachAt.get(i) });
    }

    // Sorted by line index, which is document order: the stacking pass below only ever pushes a
    // note *downward*, so it has to walk them top to bottom or one collision cascades upward.
    const placements: MarginPlacement[] = [];
    const unplaceable: DiffMarginNote[] = [];
    for (const { note, lineIndex } of anchors) {
      const rowKey = lineIndex === -1 ? undefined : rowKeyOfLine.get(lineIndex);
      if (rowKey === undefined) {
        unplaceable.push(note);
        continue;
      }
      placements.push({ key: note.key, rowKey, lineIndex, node: note.node });
    }
    placements.sort((a, b) => a.lineIndex - b.lineIndex);

    return { out, orphaned, placements, unplaceable };
  }, [lines, ranges, expanded, annotations, anchors]);

  /** The diff box itself — the scope the anchor rows are measured from. */
  const boxRef = useRef<HTMLDivElement>(null);
  /** Resolved margin geometry: where each note sits, and how tall the whole stack came out. */
  const [layout, setLayout] = useState<{ tops: Record<string, number>; height: number } | null>(null);
  /** The note under the pointer, so its anchor line can say which one it belongs to. */
  const [hovered, setHovered] = useState<string | null>(null);

  const { placements } = rows;

  /**
   * What the measure pass below actually depends on: which notes are placed, and against which row.
   *
   * Not `placements` itself, whose identity changes on every render — `rows` is memoized partly on
   * `annotations`, which a caller rebuilds inline, so an identity dep would mean a forced layout
   * read on every keystroke in a comment composer. Heights changing without this string changing is
   * the `ResizeObserver`'s job, and a note's content is fixed by its key.
   */
  const placementSignature = placements.map((p) => `${p.key}@${p.rowKey}`).join('|');

  /**
   * Position the margin after layout, because nothing here is knowable in advance: a note's height
   * depends on how its text wrapped at the width it actually got.
   *
   * `useLayoutEffect` rather than `useEffect` so the correction lands in the same paint — notes are
   * absolutely positioned with no `top` until measured, which would otherwise be one frame of them
   * piled at the top of the column.
   */
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || !marginHost || placements.length === 0) {
      setLayout(null);
      return;
    }

    const measure = () => {
      // Queried by attribute rather than held in a ref map: the rows are rebuilt on every fold, and
      // a ref map would need invalidating in step with them. Rows come from the diff box and notes
      // from the host, which are two different subtrees now that the margin is portalled.
      const byRow = new Map<string, HTMLElement>();
      box.querySelectorAll<HTMLElement>('[data-diff-row]').forEach((el) => {
        const key = el.dataset.diffRow;
        if (key) byRow.set(key, el);
      });
      const byNote = new Map<string, HTMLElement>();
      marginHost.querySelectorAll<HTMLElement>('[data-margin-note]').forEach((el) => {
        const key = el.dataset.marginNote;
        if (key) byNote.set(key, el);
      });

      // Offsets are relative to the host, because that is what the notes are positioned inside.
      const base = marginHost.getBoundingClientRect().top;
      const tops: Record<string, number> = {};
      let cursor = 0;
      for (const placement of placements) {
        const row = byRow.get(placement.rowKey);
        const note = byNote.get(placement.key);
        if (!row || !note) continue;
        // Level with its anchor, unless the note above has not finished by then.
        const top = Math.max(row.getBoundingClientRect().top - base, cursor);
        tops[placement.key] = top;
        cursor = top + note.getBoundingClientRect().height + MARGIN_GAP;
      }

      // Bail out when nothing moved. The host's own min-height comes from this measurement, so
      // applying it resizes the host and wakes the observer below — without this guard that is an
      // endless round of identical measurements.
      setLayout((prev) => {
        if (
          prev &&
          prev.height === cursor &&
          Object.keys(tops).length === Object.keys(prev.tops).length &&
          Object.entries(tops).every(([key, top]) => prev.tops[key] === top)
        ) {
          return prev;
        }
        return { tops, height: cursor };
      });
    };

    measure();
    // Width decides how every note wraps, and folds change where the anchors are. Both subtrees
    // are watched: the box's height moves the anchors, the host's width re-wraps the notes.
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(marginHost);
    return () => observer.disconnect();
    // placements is read through the closure on purpose — see placementSignature above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementSignature, marginHost]);

  const hoveredLine = hovered === null ? null : placements.find((p) => p.key === hovered)?.lineIndex ?? null;
  const hasMargin = placements.length > 0;

  return (
    <>
      <div
        ref={boxRef}
        className="rounded-md border border-border bg-card overflow-hidden font-mono text-[12.5px] leading-[1.65]"
      >
        {rows.out.map((row) => row.kind === 'gap' ? (
          <ExpanderRow
            key={row.key}
            rowKey={row.key}
            count={row.count}
            oldFirst={row.oldFirst}
            newFirst={row.newFirst}
            onExpandAll={() => reveal(row.rangeKey, { down: row.count })}
            onExpandDown={() => reveal(row.rangeKey, { down: Math.min(EXPAND_STEP, row.count) })}
            onExpandUp={() => reveal(row.rangeKey, { up: Math.min(EXPAND_STEP, row.count) })}
          />
        ) : (
          <DiffLineRow
            key={row.key}
            rowKey={row.key}
            line={row.line}
            after={row.after}
            onCommentLine={onCommentLine}
            highlighted={hoveredLine === row.index}
          />
        ))}
        {rows.orphaned.length > 0 && (
          <div className="border-t border-amber-300/80 dark:border-amber-700/50 border-l-[3px] border-l-amber-500 bg-amber-100/70 dark:bg-amber-900/25 px-3.5 py-2.5 font-sans space-y-2 shadow-inner">
            <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-500 uppercase tracking-[0.07em]">
              Comments on lines not shown above
            </p>
            {rows.orphaned}
          </div>
        )}
        {/* A note quoting text this diff does not contain — evidence for a passage the edit left
            alone, most often. Kept inside the box, because the alternative is not showing it. */}
        {rows.unplaceable.length > 0 && (
          <div className="border-t border-border-subtle bg-surface-subtle px-3.5 py-2.5 font-sans space-y-2">
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">
              Evidence for text not shown above
            </p>
            {rows.unplaceable.map((note) => (
              <div key={note.key}>{note.node}</div>
            ))}
          </div>
        )}
      </div>

      {/* The margin, rendered into the caller's host so it can sit outside whatever container the
          diff lives in. Notes are absolute at `md` and up and in normal flow below it, where a
          fixed column beside a phone-width diff would leave neither readable — the measured `top`s
          are simply inert there, a statically-positioned element ignoring them.

          The stack height reserves room for notes pushed below the last diff line, and rides a
          custom property because it must not apply below `md` either: in normal flow the notes
          already occupy exactly the height they need. */}
      {hasMargin && marginHost
        ? createPortal(
            <div
              // No `relative` of its own: the notes must resolve against the *host*, which is what
              // their tops were measured from. A positioned wrapper here would work only because
              // it happens to share the host's top edge.
              className="space-y-2 md:space-y-0 md:min-h-[var(--kb-margin-height,0px)]"
              style={layout ? ({ '--kb-margin-height': `${layout.height}px` } as CSSProperties) : undefined}
            >
              {placements.map((placement) => (
                <div
                  key={placement.key}
                  data-margin-note={placement.key}
                  onMouseEnter={() => setHovered(placement.key)}
                  onMouseLeave={() => setHovered((k) => (k === placement.key ? null : k))}
                  className={cn(
                    'md:absolute md:left-0 md:right-0',
                    layout && 'md:transition-[top] md:duration-150 md:ease-out',
                  )}
                  style={layout?.tops[placement.key] !== undefined ? { top: layout.tops[placement.key] } : undefined}
                >
                  {placement.node}
                </div>
              ))}
            </div>,
            marginHost,
          )
        : null}
    </>
  );
}

/**
 * One diff line: tinted dual line-number gutter, +/- marker, word-level highlight when the line was
 * replaced. GitHub-shaped in three ways the plainer first version was not: the number gutter carries
 * the line's colour (a deeper tint than the code area, so the gutter reads as its own column), the
 * comment affordance is a blue "+" pill that appears over the gutter edge on hover, and the comment
 * stripe beneath a line is amber — the line itself keeps its diff colour.
 */
function DiffLineRow({
  line: l,
  rowKey,
  after,
  onCommentLine,
  highlighted,
}: {
  line: DiffLine;
  /** Published as `data-diff-row` so the margin can measure where this line sits. */
  rowKey: string;
  after?: ReactNode[];
  onCommentLine?: (target: DiffLineTarget) => void;
  /** True while the pointer is on the margin note anchored here — which note goes with which line
   *  is otherwise guesswork once a note has been pushed down past its neighbours. */
  highlighted?: boolean;
}) {
  // The line keeps its own diff colour whatever hangs beneath it — a commented added line is
  // still an added line. The amber lives only on the comment stripe below.
  const rowBg =
    l.type === 'add'
      ? 'bg-green-50 dark:bg-green-950/30'
      : l.type === 'remove'
        ? 'bg-red-50 dark:bg-red-950/40'
        : undefined;

  const gutter = cn(
    'w-9 shrink-0 text-right pr-2 select-none tabular-nums',
    l.type === 'add'
      ? 'bg-green-100/80 dark:bg-green-900/40 text-green-700 dark:text-green-500'
      : l.type === 'remove'
        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
        : 'bg-surface-subtle/60 text-ink-muted',
  );

  return (
    <>
      <div
        data-diff-row={rowKey}
        className={cn(
          'group relative flex',
          rowBg,
          highlighted && 'ring-1 ring-inset ring-orange-400 dark:ring-orange-500 bg-orange-50 dark:bg-orange-950/30',
        )}
      >
        <span className={gutter}>{l.oldLineNo ?? ''}</span>
        <span className={cn(gutter, 'border-r border-border-subtle')}>{l.newLineNo ?? ''}</span>
        <span
          className={cn(
            'w-5 shrink-0 text-center select-none font-semibold',
            l.type === 'add' && 'text-green-600 dark:text-green-400',
            l.type === 'remove' && 'text-red-600 dark:text-red-400',
            l.type === 'same' && 'text-ink-faint',
          )}
        >
          {l.type === 'add' ? '+' : l.type === 'remove' ? '-' : ' '}
        </span>
        {/* The comment affordance: a blue "+" pill over the gutter edge, GitHub-shaped — overlaid
            rather than a column of its own so enabling comments never shifts the code. Pinned to
            this line's verbatim text; empty lines are excluded, a quote of nothing anchors nothing. */}
        {onCommentLine && l.text.trim().length > 0 && (
          <button
            onClick={() => onCommentLine({ quote: l.text, removed: l.type === 'remove' })}
            title="Comment on this line"
            aria-label="Comment on this line"
            className="absolute left-[63px] top-1/2 z-10 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center rounded bg-blue-600 text-white shadow-sm opacity-0 transition-all duration-100 group-hover:opacity-100 focus-visible:opacity-100 hover:scale-110 hover:bg-blue-500"
          >
            <Plus size={13} strokeWidth={3} />
          </button>
        )}
        <span className={cn('flex-1 whitespace-pre-wrap break-words pr-3 py-0.5', l.type === 'remove' ? 'text-red-950 dark:text-red-100' : 'text-ink-secondary')}>
          {l.wordTokens
            ? l.type === 'remove' ? <OldLineWords tokens={l.wordTokens} /> : <NewLineWords tokens={l.wordTokens} />
            : (l.text.length > 0 ? l.text : ' ')}
        </span>
      </div>
      {after && after.length > 0 && (
        <div className="border-y border-amber-300/80 dark:border-amber-700/50 border-l-[3px] border-l-amber-500 bg-amber-100/70 dark:bg-amber-900/25 px-3.5 py-2.5 font-sans space-y-2 shadow-inner">
          {after}
        </div>
      )}
    </>
  );
}
